import debounce from "lodash.debounce";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import {
  fetchGet,
  fetchPost,
  toQueryString,
} from "../../../../service/api_client";
import AutoCompleteField from "../../../../ui/new_components/common/autocomplete";
import { ItemModelV2 } from "../../../../erp/inventory/models/item_model";
import { ItemWarehouseModel } from "../model/warehouse_outward_model";

interface ItemWarehouseSearchFieldProps {
  value?: ItemWarehouseModel;
  disabled?: boolean;
  placeholder?: string;
  item_ids?: string[];
  onSelect: (item: ItemWarehouseModel, raw: ItemModelV2) => void;
  id?: string;
}

const ItemWarehouseSearchField: FunctionComponent<
  ItemWarehouseSearchFieldProps
> = ({ value, disabled, placeholder, item_ids, onSelect, id }) => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/item_ids`;
  const { showToast } = useToast();

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [data, setData] = useState<ItemModelV2[]>();

  /** ---------------- Fetch ---------------- */
  const getData = async (params: {
    search?: string;
    page: number;
    count: number;
    all?: boolean;
    item_ids?: string[];
  }) => {
    setLoading(true);
    try {
      const isLargeRequest = params.item_ids && params.item_ids.length > 100;
      const requestUrl = isLargeRequest ? url : url + toQueryString(params);

      const res = isLargeRequest
        ? await fetchPost(requestUrl, params)
        : await fetchGet(requestUrl);

      if (!res?.success) {
        showToast({ type: ToastType.error, text: res?.error });
        return;
      }

      setData(res.data.data);
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err?.message || "Failed to search items",
      });
    } finally {
      setLoading(false);
    }
  };

  /** ---------------- Debounced Search ---------------- */
  const debouncedSearch = useCallback(
    debounce((query: string, ids?: string[]) => {
      if (!query) {
        setData(undefined);
        return;
      }

      getData({
        search: query.trim(),
        page: 1,
        count: 10,
        all: true,
        item_ids: ids,
      });
    }, 400),
    [],
  );

  /** ---------------- Effects ---------------- */
  useEffect(() => {
    if (edit && search) {
      debouncedSearch(search, item_ids);
    }
  }, [search, item_ids, edit]);

  useEffect(() => {
    if (value?.itemName) {
      setSearch(
        `${value.itemName}${value.inventoryItemCode ? ` - ${value.inventoryItemCode}` : ""}`,
      );
    }
  }, [value]);

  useEffect(() => {
    if (disabled) {
      setEdit(false);
    }
  }, [disabled]);

  /** ---------------- Map Item ---------------- */
  const mapToWarehouseItem = (item: ItemModelV2): ItemWarehouseModel => ({
    inventoryItemCode: item.item_code || "",
    itemName: item.name || "",
    requestedQty: 1,
    itemPrice: 0,
    itemUnit: item.unit?.formal_name || "",
    subCategory: item.subcategory || "",
    itemHSN: item.tax_detail?.hsn_sac || "",
    deviceIds: [],
    gst_applicable: item.tax_applicable || false,
    tax_detail: {
      igst: item.tax_detail?.gst_rate ? item.tax_detail.gst_rate / 2 : 0,
      cgst: item.tax_detail?.gst_rate ? item.tax_detail.gst_rate / 2 : 0,
      sgst: item.tax_detail?.gst_rate ? item.tax_detail.gst_rate / 2 : 0,
    },
  });

  return (
    <div className="w-full" id={id}>
      {edit ? (
        <AutoCompleteField<ItemModelV2>
          id={id ? `${id}-input` : undefined}
          disabled={disabled}
          loading={loading}
          value={search}
          placeholder={placeholder ?? "Search item"}
          onChange={setSearch}
          onSelect={(val) => {
            setEdit(false);
            setSearch(val.value.name);

            const mapped = mapToWarehouseItem(val.value);
            onSelect(mapped, val.value);
          }}
          suggestions={data?.map((d) => ({
            label: (
              <div className="text-xs">
                <b>{d.name}</b>
                {d.description && (
                  <span className="text-gray-400"> – {d.description}</span>
                )}
              </div>
            ),
            value: d,
          }))}
        />
      ) : (
        <div
          id={id ? `${id}-display` : undefined}
          onClick={() => !disabled && setEdit(true)}
          title={search}
          className={`text-xs px-3 py-2 h-8 rounded w-full border cursor-text truncate ${
            search ? "" : "text-gray-400"
          }`}
        >
          {search || placeholder || "Select item"}
        </div>
      )}
    </div>
  );
};

export default ItemWarehouseSearchField;
